import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Play, Pause, Trash2, RefreshCw, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { 
  getCronJobs, 
  getCronJobHistory, 
  toggleCronJob, 
  deleteCronJob,
  disableAllCrons,
  type CronJob,
  type CronJobRun 
} from '@/services/cronJobMonitoring';
import { formatDistanceToNow } from 'date-fns';

export function CronJobMonitor() {
  const queryClient = useQueryClient();

  const { data: jobs, isLoading: jobsLoading } = useQuery({
    queryKey: ['cron-jobs'],
    queryFn: getCronJobs,
    refetchInterval: 30000, // Refresh every 30s
  });

  const { data: history, isLoading: historyLoading } = useQuery({
    queryKey: ['cron-history'],
    queryFn: () => getCronJobHistory(20),
    refetchInterval: 30000,
  });

  const toggleMutation = useMutation({
    mutationFn: ({ jobname, active }: { jobname: string; active: boolean }) =>
      toggleCronJob(jobname, active),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cron-jobs'] });
      toast.success('Cron job updated');
    },
    onError: (error) => {
      toast.error(`Failed to update cron job: ${error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteCronJob,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cron-jobs'] });
      toast.success('Cron job deleted');
    },
    onError: (error) => {
      toast.error(`Failed to delete cron job: ${error.message}`);
    },
  });

  const disableAllMutation = useMutation({
    mutationFn: disableAllCrons,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cron-jobs'] });
      toast.success('All automation stopped');
    },
    onError: (error) => {
      toast.error(`Failed to stop automation: ${error.message}`);
    },
  });

  const getJobStatus = (job: CronJob) => {
    if (!job.active) return { color: 'secondary', label: 'Paused' };
    return { color: 'default', label: 'Active' };
  };

  const getRunStatus = (status: string) => {
    if (status === 'succeeded') return 'default';
    if (status === 'failed') return 'destructive';
    return 'secondary';
  };

  if (jobsLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const activeJobs = jobs?.filter(j => j.active).length || 0;
  const totalJobs = jobs?.length || 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Cron Job Monitor</h2>
          <p className="text-muted-foreground">
            {activeJobs} of {totalJobs} jobs active
          </p>
        </div>
        <Button
          variant="destructive"
          size="sm"
          onClick={() => disableAllMutation.mutate()}
          disabled={disableAllMutation.isPending}
        >
          {disableAllMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <AlertTriangle className="h-4 w-4 mr-2" />
          )}
          Emergency Stop All
        </Button>
      </div>

      <Alert>
        <AlertDescription>
          Monitor and control automated background jobs. Use caution when modifying active jobs.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle>Active Jobs</CardTitle>
          <CardDescription>Configure and monitor scheduled background tasks</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {jobs?.map((job) => {
              const status = getJobStatus(job);
              return (
                <div
                  key={job.jobid}
                  className="flex items-center justify-between p-4 border rounded-lg"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{job.jobname}</span>
                      <Badge variant={status.color as any}>{status.label}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Schedule: {job.schedule}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={job.active}
                      onCheckedChange={(checked) =>
                        toggleMutation.mutate({ jobname: job.jobname, active: checked })
                      }
                      disabled={toggleMutation.isPending}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deleteMutation.mutate(job.jobname)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Execution History</CardTitle>
          <CardDescription>Recent cron job executions</CardDescription>
        </CardHeader>
        <CardContent>
          {historyLoading ? (
            <div className="flex justify-center p-4">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <div className="space-y-2">
              {history?.slice(0, 10).map((run) => (
                <div
                  key={run.runid}
                  className="flex items-center justify-between p-3 border rounded-lg text-sm"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Badge variant={getRunStatus(run.status) as any}>
                        {run.status}
                      </Badge>
                      <span className="font-mono text-xs">{run.jobid}</span>
                    </div>
                    {run.return_message && (
                      <p className="text-xs text-muted-foreground mt-1 truncate max-w-md">
                        {run.return_message}
                      </p>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(run.start_time), { addSuffix: true })}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
